"use client";

import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetStartedWithItInfrastructure() {
  return <section data-section-id="33596" id="contact" className="bg-background py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-[20px] font-mono font-bold text-primary tracking-widest uppercase">
            OPERATIONS@VENCHANDLERSNEXUS.COM
          </h2>
        </div>

        <FormProvider formKey="contact_form_cookiespolicy" formId="contact_form_cookiespolicy" sectionName="get-started-with-it-infrastructure" defaultValues={{
        full_name: "",
        email_address: "",
        phone_number: "",
        company_name: "",
        it_requirements: "",
        budget_range: "",
        gdpr_consent: false
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2
        },
        email_address: {
          required: true,
          pattern: "email"
        },
        phone_number: {
          required: true,
          minLength: 5
        },
        company_name: {
          required: false
        },
        it_requirements: {
          required: true,
          minLength: 10
        },
        budget_range: {
          required: false
        },
        gdpr_consent: {
          required: true
        }
      }} submitText="SUBMIT MANIFEST" successMessage="Manifest submitted. Our logistics team will review your inquiry." errorMessage="Submission failed. Please check your connection and try again." className="w-full">
          <InfrastructureForm />
        </FormProvider>
      </div>
    </section>;
}
function InfrastructureForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const getAnimationDelay = (index: number) => ({
    transitionDelay: `${index * 100}ms`
  });
  const animationClass = `transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-y-0 [clip-path:inset(0_0_0_0)]" : "opacity-0 translate-y-4 [clip-path:inset(0_100%_0_0)]"}`;
  const inputClasses = "bg-sidebar border-0 border-b border-secondary text-foreground placeholder:text-muted-foreground/40 focus:border-primary focus:ring-0 rounded-none transition-all duration-300 h-12 shadow-none";
  const labelClasses = "text-muted-foreground font-mono font-medium tracking-widest text-xs uppercase";
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-accent/30 border border-primary/20 rounded-none text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-xl font-mono font-bold text-primary mb-2">Manifest Logged</h3>
        <p className="text-muted-foreground mb-6 font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-secondary text-secondary hover:text-primary hover:border-primary rounded-none font-mono">
          New Submission
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6" ref={ref}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <div className={animationClass} style={getAnimationDelay(0)} data-index={0}>
          <FormField control={control} name="full_name" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClasses}>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="ENTER NAME" {...field} className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive text-xs font-mono" />
              </FormItem>} />
        </div>

        {}
        <div className={animationClass} style={getAnimationDelay(1)} data-index={1}>
          <FormField control={control} name="email_address" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClasses}>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="CONTACT@DOMAIN.COM" {...field} className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive text-xs font-mono" />
              </FormItem>} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <div className={animationClass} style={getAnimationDelay(2)} data-index={2}>
          <FormField control={control} name="phone_number" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClasses}>Contact Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+966 5X XXX XXXX" {...field} className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive text-xs font-mono" />
              </FormItem>} />
        </div>

        {}
        <div className={animationClass} style={getAnimationDelay(3)} data-index={3}>
          <FormField control={control} name="company_name" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClasses}>Organization</FormLabel>
                <FormControl>
                  <Input placeholder="COMPANY NAME" {...field} className={inputClasses} />
                </FormControl>
                <FormMessage className="text-destructive text-xs font-mono" />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className={animationClass} style={getAnimationDelay(4)} data-index={4}>
        <FormField control={control} name="budget_range" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Service Category</FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className={`w-full px-3 py-1 text-base md:text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 appearance-none ${inputClasses}`}>
                    <option value="" disabled className="bg-background text-muted-foreground">
                      SELECT CATEGORY
                    </option>
                    <option value="Global Freight" className="bg-background text-foreground">
                      Global Freight
                    </option>
                    <option value="Supply Chain Management" className="bg-background text-foreground">
                      Supply Chain Management
                    </option>
                    <option value="Warehousing" className="bg-background text-foreground">
                      Warehousing
                    </option>
                    <option value="Customs Clearance" className="bg-background text-foreground">
                      Customs Clearance
                    </option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="text-destructive text-xs font-mono" />
            </FormItem>} />
      </div>

      {}
      <div className={animationClass} style={getAnimationDelay(5)} data-index={5}>
        <FormField control={control} name="it_requirements" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Inquiry Details</FormLabel>
              <FormControl>
                <textarea {...field} placeholder="SPECIFY YOUR CARGO OR LOGISTICS REQUIREMENTS..." className={`w-full min-h-[120px] px-3 py-2 text-base md:text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-y ${inputClasses} h-auto`} />
              </FormControl>
              <FormMessage className="text-destructive text-xs font-mono" />
            </FormItem>} />
      </div>

      {}
      <div className={animationClass} style={getAnimationDelay(6)} data-index={6}>
        <FormField control={control} name="gdpr_consent" rules={{
        required: true
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border border-secondary/20 bg-sidebar/50">
              <FormControl>
                <input type="checkbox" className="mt-1 size-4 rounded-none border-secondary bg-background text-primary focus:ring-0 focus:ring-offset-0 accent-primary cursor-pointer" checked={field.value} onChange={field.onChange} required />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground cursor-pointer">
                  I AGREE TO THE PROCESSING OF MY DATA ACCORDING TO THE{" "}
                  <Link to="/PrivacyPolicyPage" className="text-primary hover:underline underline-offset-4">
                    PRIVACY POLICY
                  </Link>
                </FormLabel>
                <FormMessage className="text-destructive text-[10px] font-mono" />
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-none animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-xs text-center font-mono">{submitError}</p>
        </div>}

      <div className={`${animationClass} pt-4`} style={getAnimationDelay(7)} data-index={7}>
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-transparent border border-secondary text-secondary hover:text-primary hover:border-primary font-mono font-bold tracking-[0.3em] uppercase text-lg shadow-none transition-all duration-300 rounded-none">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 h-5 w-5" />
              PROCESSING...
            </> : "SUBMIT MANIFEST"}
        </Button>
      </div>
    </Form>;
}